import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FormField, type FieldOption } from '@noor/ui';
import { cn } from '@noor/lib/cn';
import { useFormContext } from 'react-hook-form';
import { UNIT_CODES } from '../config/unit-codes';
import { IKPU_CODE_LENGTH, type ProductFormValues } from '../model/schema';
import { MeasureAmountField } from './measure-amount-field';
import { MeasurementTypeField } from './measurement-type-field';
import { VatPercentField } from './vat-percent-field';

/**
 * The OFD (fiscal receipt) classifiers a product carries — IKPU/SPIC code, the
 * goods-marking switch, and the two fields that only apply to a marked
 * product: its measurement unit and its Data Matrix code. Added to the product
 * write bodies in API v1.00.155.
 *
 * The group is always visible: it is part of the form, not a disclosure. Only
 * the marking-dependent pair is conditional, and on the switch alone — nothing
 * here hides behind a collapsed header a merchant has to discover.
 */
export function FiscalFields({ className }: { className?: string }) {
  const { t } = useTranslation('eats');
  const form = useFormContext<ProductFormValues>();

  // Watched rather than read once: the unit select and the Data Matrix input
  // appear and disappear with the switch, so this has to re-render on toggle.
  const hasMarkedCode = form.watch('hasMarkedCode') === true;

  // Turning marking OFF clears both dependent fields, mirroring how the drawer
  // treats prep-time when "always ready" is on: a hidden field must never
  // submit a stale value. Without this an unmarked product would keep sending
  // the unit and Data Matrix code the merchant can no longer see or edit.
  //
  // Cleared to `''` rather than `undefined`: the two inputs UNMOUNT when the
  // switch goes off, and RHF re-registers a remounted field from its stored
  // default — which `setValue(undefined)` leaves untouched, so the old code
  // came back the moment marking was switched on again. An empty string is a
  // real value that overwrites it, and the form schema maps `''` back to
  // `undefined` on submit (`emptyToUndefined`), so the API layer still omits
  // the field rather than blanking a stored classifier.
  const { setValue, clearErrors } = form;
  useEffect(() => {
    if (hasMarkedCode) return;
    setValue('markedCode', '');
    clearErrors(['markedCode']);
  }, [hasMarkedCode, setValue, clearErrors]);

  const unitOptions = useMemo<FieldOption[]>(
    () =>
      UNIT_CODES.map(({ code, labelKey }) => ({
        value: code,
        label: t(`menu.product.form.units.${labelKey}`),
      })),
    [t],
  );

  const ikpuCode = form.watch('ikpuCode');
  const ikpuLength = typeof ikpuCode === 'string' ? ikpuCode.length : 0;

  return (
    <section className={cn('flex flex-col gap-3', className)}>
      <div className="flex flex-col gap-0.5">
        <span className="text-sm font-medium tracking-tight text-foreground">
          {t('menu.product.form.fiscalTitle')}
        </span>
        <span className="text-xs leading-relaxed text-muted-foreground">
          {t('menu.product.form.fiscalHint')}
        </span>
      </div>

      <FormField name="ikpuCode" className="gap-1.5">
        <FormField.Label className="flex items-center gap-0.5 text-sm font-medium tracking-tight">
          {t('menu.product.form.ikpuLabel')}
          <span className="text-primary">*</span>
        </FormField.Label>
        <FormField.Masked
          mask={'#'.repeat(IKPU_CODE_LENGTH)}
          className={FISCAL_FIELD}
          placeholder={t('menu.product.form.ikpuPlaceholder')}
        />
        <div className="flex items-center justify-between">
          <FormField.Message />
          <span className="ml-auto text-xs text-muted-foreground tabular-nums">
            {ikpuLength}/{IKPU_CODE_LENGTH}
          </span>
        </div>
      </FormField>

      <FormField name="unitCode" className="gap-1.5">
        <FormField.Label className="flex items-center gap-0.5 text-sm font-medium tracking-tight">
          {t('menu.product.form.unitCodeLabel')}
          <span className="text-primary">*</span>
        </FormField.Label>
        <FormField.Select
          className={FISCAL_FIELD}
          options={unitOptions}
          placeholder={t('menu.product.form.unitCodePlaceholder')}
        />
        <FormField.Message />
      </FormField>

      <MeasureAmountField className="gap-1.5" fieldClassName={FISCAL_FIELD} />

      <MeasurementTypeField className="gap-1.5" fieldClassName={FISCAL_FIELD} />

      <VatPercentField className="gap-1.5" fieldClassName={FISCAL_FIELD} />

      <FormField name="hasMarkedCode" className="py-1">
        <FormField.Switch
          label={t('menu.product.form.hasMarkedCode')}
          className="[&_label]:text-sm [&_label]:font-medium [&_label]:tracking-tight [&_label]:text-foreground"
        />
      </FormField>

      {hasMarkedCode && (
        <FormField name="markedCode" className="gap-1.5">
          <FormField.Label className="flex items-center gap-0.5 text-sm font-medium tracking-tight">
            {t('menu.product.form.markedCodeLabel')}
            <span className="text-primary">*</span>
          </FormField.Label>
          <FormField.Input
            className={FISCAL_FIELD}
            autoComplete="off"
            placeholder={t('menu.product.form.markedCodePlaceholder')}
          />
          <FormField.Message />
        </FormField>
      )}
    </section>
  );
}

/** Same white bordered pill the drawer's other inputs use. */
const FISCAL_FIELD =
  'rounded-3xl border-input bg-card shadow-xs focus-visible:border-input-border-focus';
